function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useRef, useState } from 'react';
import cn from 'clsx';
import { string, any, func, oneOfType, shape } from 'prop-types';
import { useTranslation } from 'react-i18next';
import FormInput from '../../atoms/FormInput/index.jsx';
import { useOnClickOutside } from '../../hooks/index.js';
import { debounce, getIndexFromKeycode } from '../../utils/index.js';
import Link from '../../atoms/Link/index.jsx';

/**
 * FormAutocomplete component.
 */
const FormAutocomplete = ({
  className,
  name,
  formInputRef,
  autocomplete,
  onChange,
  ...restProps
}) => {
  var _options$selected2;
  const dropdownRef = useRef();
  const [selected, setSelected] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const [options, setOptions] = useState([]);
  const {
    t
  } = useTranslation();
  const listBoxId = `${name}-listbox`;
  const minimumCharacters = 3;
  const searchByText = debounce(input => {
    autocomplete(input).then(res => res.input === formInputRef.current.value && setOptions(res.results)).catch(() => setShowDropdown(false)); // rejected request
  }, 300);

  /**
   * Handle a change of text in the search input.
   */
  const onTextInputChange = evt => {
    const {
      value
    } = evt.target;
    const hasEnoughChars = value.length >= minimumCharacters;
    setSelected(-1);
    if (hasEnoughChars) {
      searchByText(value);
    }
    setShowDropdown(hasEnoughChars);
    onChange(evt);
  };
  const onTextInputKeyDown = e => {
    const {
      keyCode
    } = e;

    // Skip other than related key actions
    if (![40, 38, 36, 35, 13, 27, 9].includes(keyCode)) {
      return;
    }
    if (!showDropdown) {
      // Down - open dropdown
      if (keyCode === 40) {
        setShowDropdown(true);
        setSelected(0);
      }

      // From here on are only dropdown related key actions
      return;
    }

    // Esc, tab - Close the dropdown
    if (keyCode === 27 || keyCode === 9) {
      setShowDropdown(false);
      return;
    }

    // Enter - Open selected
    if (keyCode === 13 && selected >= 0) {
      var _options$selected;
      window.open((_options$selected = options[selected]) === null || _options$selected === void 0 ? void 0 : _options$selected.url);
      return;
    }

    // Select next option in dropdown
    const lastIndex = options.length - 1;
    const nextIndex = getIndexFromKeycode(keyCode, selected, lastIndex);
    setSelected(nextIndex);
  };

  // Hide on click outside the dropdown.
  useOnClickOutside(dropdownRef, () => {
    if (!showDropdown) {
      return;
    }
    setShowDropdown(false);
  }, [showDropdown]);
  const listItem = (option, i) => /*#__PURE__*/React.createElement("li", {
    id: option.id,
    key: option.id,
    className: "form-autocomplete__list-item d-flex" + (selected === i ? " form-autocomplete__list-item--selected" : ""),
    role: "option",
    "aria-selected": selected === i,
    tabIndex: "-1"
  }, /*#__PURE__*/React.createElement(Link, {
    href: option === null || option === void 0 ? void 0 : option.url,
    ellipsis: true,
    onKeyDown: onTextInputKeyDown
  }, option.title));
  return /*#__PURE__*/React.createElement("div", {
    className: cn('form-autocomplete', className)
  }, /*#__PURE__*/React.createElement(FormInput, _extends({}, restProps, {
    ref: formInputRef,
    name: name,
    className: "form-autocomplete__input",
    role: "searchbox",
    "aria-haspopup": "listbox",
    "aria-controls": listBoxId,
    "aria-activedescendant": (_options$selected2 = options[selected]) === null || _options$selected2 === void 0 ? void 0 : _options$selected2.id,
    "aria-label": t('molecules.formAutoComplete.ariaLabel.search'),
    onChange: onTextInputChange,
    onKeyDown: onTextInputKeyDown,
    autoComplete: "off"
  })), /*#__PURE__*/React.createElement("div", {
    ref: dropdownRef,
    className: 'form-autocomplete__options pt-xxs pb-xxs pl-xs pr-xs' + (showDropdown ? " form-autocomplete__options--visible" : "")
  }, /*#__PURE__*/React.createElement("ul", {
    id: listBoxId,
    className: "ma-reset pa-reset",
    role: "listbox",
    "aria-label": "options"
  }, options.map(listItem)), !options.length && /*#__PURE__*/React.createElement("div", {
    className: "form-autocomplete__list-item form-autocomplete__no-options-item body-copy-small"
  }, t('molecules.formAutoComplete.ariaLabel.noResult'))));
};
FormAutocomplete.displayName = 'FormAutocomplete';
FormAutocomplete.defaultProps = {
  className: null,
  name: 'autocomplete',
  formInputRef: null,
  autocomplete: null,
  onChange: () => null
};
FormAutocomplete.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,
  /** A name for the input. */
  name: string,
  /** A ref for the form input. */
  formInputRef: oneOfType([func, shape({
    current: any
  })]),
  /** A fetch API function. */
  autocomplete: func,
  /** On change event handler */
  onChange: func
} : {};
export default FormAutocomplete;